import React, { useState, useEffect, useCallback } from 'react';
import { Package, Check, X, Clock, Search, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export default function AdminRedemptions() {
  const { token, API } = useAuth();
  const [redemptions, setRedemptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [fulfillDialog, setFulfillDialog] = useState(null);
  const [fulfillCode, setFulfillCode] = useState('');
  const [fulfillNote, setFulfillNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRedemptions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/redemptions`, {
        headers, params: { status: filter }
      });
      setRedemptions(res.data.redemptions);
      setTotal(res.data.total);
      setPendingCount(res.data.pending_count);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token, filter]);

  useEffect(() => { fetchRedemptions(); }, [fetchRedemptions]);

  const handleFulfill = async () => {
    if (!fulfillDialog) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/redemptions/${fulfillDialog.id}/fulfill`, {
        fulfillment_code: fulfillCode,
        admin_note: fulfillNote,
      }, { headers });
      setFulfillDialog(null);
      setFulfillCode('');
      setFulfillNote('');
      fetchRedemptions();
    } catch (e) { console.error(e); }
    setProcessing(false);
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this redemption and refund points?')) return;
    try {
      await axios.put(`${API}/admin/redemptions/${id}/cancel`, {}, { headers });
      fetchRedemptions();
    } catch (e) { console.error(e); }
  };

  const statusBadge = (status) => {
    if (status === 'pending') return <Badge variant="outline" className="border-yellow-500/30 text-yellow-400 text-[10px]">Pending</Badge>;
    if (status === 'fulfilled') return <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[10px]">Fulfilled</Badge>;
    if (status === 'cancelled') return <Badge variant="outline" className="border-red-500/30 text-red-400 text-[10px]">Cancelled</Badge>;
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-white">Redemptions</h2>
          <p className="text-sm text-zinc-500">
            {pendingCount > 0 ? <span className="text-yellow-400 font-bold">{pendingCount} pending</span> : 'No pending'} redemptions &middot; {total} total
          </p>
        </div>
        <div className="flex gap-2">
          {['', 'pending', 'fulfilled', 'cancelled'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filter === f ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-zinc-500 border border-transparent hover:text-zinc-300'
              }`}
              data-testid={`filter-${f || 'all'}`}
            >
              {f || 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : redemptions.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No redemptions found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {redemptions.map(r => (
            <div key={r.id} className="bg-[#121214] border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-redemption-${r.id}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm ${
                r.status === 'pending' ? 'bg-yellow-500/10' : r.status === 'fulfilled' ? 'bg-emerald-500/10' : 'bg-red-500/10'
              }`}>
                {r.status === 'pending' ? <Clock className="w-4 h-4 text-yellow-400" /> : r.status === 'fulfilled' ? <Check className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-red-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-bold text-white">{r.reward_name}</span>
                  <span className="text-xs text-zinc-500">{r.tier_label}</span>
                  {statusBadge(r.status)}
                </div>
                <p className="text-xs text-zinc-500">
                  {r.user_name} ({r.user_email}) &middot; {r.points_spent.toLocaleString()} pts &middot; {new Date(r.created_at).toLocaleDateString()}
                </p>
                {r.fulfillment_code && (
                  <p className="text-[10px] text-emerald-400/70 font-mono mt-1">Code: {r.fulfillment_code}</p>
                )}
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => { setFulfillDialog(r); setFulfillCode(''); setFulfillNote(''); }}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs h-8 gap-1"
                    data-testid={`fulfill-btn-${r.id}`}
                  >
                    <Check className="w-3 h-3" /> Fulfill
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCancel(r.id)}
                    className="text-red-400 hover:text-red-300 text-xs h-8 gap-1"
                    data-testid={`cancel-btn-${r.id}`}
                  >
                    <X className="w-3 h-3" /> Cancel
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Fulfill Dialog */}
      <Dialog open={!!fulfillDialog} onOpenChange={() => setFulfillDialog(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Fulfill Redemption</DialogTitle>
          </DialogHeader>
          {fulfillDialog && (
            <div className="space-y-4 pt-2">
              <div className="glass-card rounded-lg p-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Reward</span>
                  <span className="text-white font-medium">{fulfillDialog.reward_name} ({fulfillDialog.tier_label})</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">User</span>
                  <span className="text-white">{fulfillDialog.user_name} ({fulfillDialog.user_email})</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Points spent</span>
                  <span className="text-amber-500 font-mono">{fulfillDialog.points_spent.toLocaleString()}</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Gift Card Code / Reference</label>
                <Input
                  value={fulfillCode}
                  onChange={e => setFulfillCode(e.target.value)}
                  placeholder="e.g. XXXX-XXXX-XXXX-XXXX"
                  className="bg-[#09090B] border-white/10 text-white font-mono"
                  data-testid="fulfill-code-input"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Admin Note (optional)</label>
                <Input
                  value={fulfillNote}
                  onChange={e => setFulfillNote(e.target.value)}
                  placeholder="Internal note..."
                  className="bg-[#09090B] border-white/10 text-white"
                />
              </div>
              <Button
                onClick={handleFulfill}
                disabled={processing}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
                data-testid="fulfill-submit-btn"
              >
                {processing ? 'Processing...' : 'Mark as Fulfilled'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
